
import React, { useState, useEffect } from 'react';
import { Reservation, Table, CategoryDefinition, RestaurantConfig, PackageBooking } from '../types';
import { X, Plus, Pencil, Minus, AlertCircle, Target, Gift, Timer, Users, CheckCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (res: Reservation) => void;
  editingReservation: Reservation | null;
  initialData?: Partial<Reservation>;
  tables: Table[];
  categories: CategoryDefinition[];
  reservations: Reservation[];
  config: RestaurantConfig;
}

export const ReservationModal: React.FC<ReservationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingReservation,
  initialData,
  tables,
  categories,
  reservations,
  config
}) => {
  const [formData, setFormData] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    time: '19:00',
    partySize: 2,
    duration: 90,
    manualTableId: '',
    notes: '',
    packageName: ''
  });

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [isPackagesExpanded, setIsPackagesExpanded] = useState(false);
  const [showExtendedInfoId, setShowExtendedInfoId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && config) {
      if (editingReservation) {
        setFormData({
          name: editingReservation.customerName,
          date: editingReservation.date,
          time: editingReservation.time,
          partySize: editingReservation.partySize,
          duration: editingReservation.durationMinutes,
          manualTableId: editingReservation.tableId,
          notes: editingReservation.notes || '',
          packageName: editingReservation.packageName || ''
        });
        const pkg = config.packages?.find(p => p.name === editingReservation.packageName);
        setSelectedPackageId(pkg?.id || null);
      } else {
        setFormData({
          name: initialData?.customerName || '',
          date: initialData?.date || new Date().toISOString().split('T')[0],
          time: initialData?.time || '19:00',
          partySize: initialData?.partySize || 2,
          duration: initialData?.durationMinutes || 90,
          manualTableId: initialData?.tableId || '',
          notes: initialData?.notes || '',
          packageName: ''
        });
        setSelectedPackageId(null);
      }
      setIsPackagesExpanded(false);
      setShowExtendedInfoId(null);
    }
  }, [isOpen, editingReservation, initialData, config?.packages]);

  const handlePackageSelect = (pkg: PackageBooking) => {
    if (selectedPackageId === pkg.id) {
      setSelectedPackageId(null);
      setFormData(prev => ({ ...prev, packageName: '', manualTableId: '' }));
      return;
    }
    setSelectedPackageId(pkg.id);
    setFormData(prev => ({
      ...prev,
      duration: pkg.durationMinutes,
      partySize: pkg.defaultPartySize,
      packageName: pkg.name,
      // Reset table selection if current table doesn't match package category
      manualTableId: tables.find(t => t.id === prev.manualTableId)?.category === pkg.categoryId ? prev.manualTableId : ''
    }));
  };

  const isTableAvailable = (tableId: string, date: string, time: string, duration: number, excludeResId?: string) => {
    const reqStart = new Date(`${date}T${time}`).getTime();
    const reqEnd = reqStart + duration * 60000;
    return !reservations.some(res => {
      if (res.id === (excludeResId || editingReservation?.id)) return false;
      if (res.tableId !== tableId || res.date !== date || res.status === 'PENDING') return false;
      const resStart = new Date(`${res.date}T${res.time}`).getTime();
      const resEnd = resStart + res.durationMinutes * 60000;
      return reqStart < resEnd && reqEnd > resStart;
    });
  };

  const handleSave = () => {
    const finalTableId = formData.manualTableId;
    if (!finalTableId) {
      alert("Bitte wählen Sie ein Spielfeld aus.");
      return;
    }
    if (!formData.name.trim()) {
      alert("Bitte geben Sie einen Namen für den Gast oder Verein ein.");
      return;
    }
    const table = tables.find(t => t.id === finalTableId);
    onSave({
      id: editingReservation?.id || Math.random().toString(36).substr(2, 9),
      tableId: finalTableId,
      categoryId: table?.category || '',
      packageName: formData.packageName || undefined,
      customerName: formData.name.trim(),
      partySize: formData.partySize,
      date: formData.date,
      time: formData.time,
      durationMinutes: formData.duration,
      notes: formData.notes,
      status: 'CONFIRMED'
    });
    onClose();
  };

  if (!isOpen || !config) return null;

  const activePackage = config.packages?.find(p => p.id === selectedPackageId);
  const filteredTables = tables.filter(t => !activePackage || t.category === activePackage.categoryId);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in-up">
        
        {/* Info Overlay inside Modal */}
        {showExtendedInfoId && (
            <div className="absolute inset-0 z-[160] bg-white animate-fade-in flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <h3 className="font-black text-slate-800 uppercase tracking-tighter italic">Details: {config.packages?.find(p => p.id === showExtendedInfoId)?.name}</h3>
                    <button onClick={() => setShowExtendedInfoId(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><X size={20}/></button>
                </div>
                <div className="flex-1 overflow-y-auto p-8 prose prose-emerald max-w-none prose-sm">
                    <div dangerouslySetInnerHTML={{ __html: config.packages?.find(p => p.id === showExtendedInfoId)?.extendedInfo || '<p class="text-slate-400 italic">Keine weiteren Details verfügbar.</p>' }} />
                </div>
                <div className="p-6 border-t border-slate-100 flex justify-end">
                    <button onClick={() => setShowExtendedInfoId(null)} className="px-6 py-2 bg-slate-900 text-white font-bold rounded-xl uppercase tracking-widest text-xs">Zurück</button>
                </div>
            </div>
        )}

        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase tracking-tighter italic">
            {editingReservation ? <Pencil className="text-blue-500" size={20} /> : <Plus className="text-emerald-500" size={20} />} 
            {editingReservation ? 'Buchung bearbeiten' : 'Neue Buchung'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500"><X size={20} /></button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basisdaten */}
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                Gast / Verein <span className="text-red-500">*</span>
              </label>
              <input type="text" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none font-bold bg-white" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Vollständiger Name..." />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Datum</label>
                <input type="date" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none font-bold bg-white" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Uhrzeit</label>
                <input type="time" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none font-bold bg-white" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Spieler</label>
                <div className={`flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white ${selectedPackageId ? 'opacity-60 grayscale' : ''}`}>
                  <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, partySize: Math.max(1, formData.partySize - 1)})} className="px-3 py-3 hover:bg-slate-50 border-r border-slate-100 text-slate-600"><Minus size={14} /></button>
                  <input type="number" disabled={!!selectedPackageId} className="flex-1 w-full text-center text-sm font-black text-slate-800 outline-none bg-white" value={formData.partySize} onChange={e => setFormData({...formData, partySize: Math.max(1, parseInt(e.target.value) || 1)})} />
                  <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, partySize: formData.partySize + 1})} className="px-3 py-3 hover:bg-slate-50 border-l border-slate-100 text-slate-600"><Plus size={14} /></button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Minuten</label>
                <div className={`flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white ${selectedPackageId ? 'opacity-60 grayscale' : ''}`}>
                  <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, duration: Math.max(15, formData.duration - 15)})} className="px-3 py-3 hover:bg-slate-50 border-r border-slate-100 text-slate-600"><Minus size={14} /></button>
                  <input type="number" step="15" disabled={!!selectedPackageId} className="flex-1 w-full text-center text-sm font-black text-slate-800 outline-none bg-white" value={formData.duration} onChange={e => setFormData({...formData, duration: Math.max(15, parseInt(e.target.value) || 15)})} />
                  <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, duration: formData.duration + 15})} className="px-3 py-3 hover:bg-slate-50 border-l border-slate-100 text-slate-600"><Plus size={14} /></button>
                </div>
              </div>
            </div>
          </div>

          {/* Notizen */}
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Notizen / Wünsche</label>
            <textarea className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none bg-white font-medium resize-none h-24" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Zusätzliche Infos zum Spiel..." />
          </div>

          {/* Spielfeldwahl (Manuell) */}
          <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                <Target size={12} className="text-emerald-600" /> Sportart & Spielfeld (Pflichtfeld)
              </label>
              <div className="relative">
                <select 
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none bg-white font-bold focus:ring-2 focus:ring-emerald-500 transition-all appearance-none pr-10" 
                  value={formData.manualTableId} 
                  onChange={e => setFormData({...formData, manualTableId: e.target.value})}
                >
                  <option value="">-- Spielfeld manuell zuweisen --</option>
                  {[...filteredTables].sort((a,b) => a.name.localeCompare(b.name, undefined, {numeric: true})).map(t => {
                    const cat = categories.find(c => c.id === t.category);
                    return (
                      <option key={t.id} value={t.id} disabled={!isTableAvailable(t.id, formData.date, formData.time, formData.duration)}>
                        {cat?.icon || '📍'} {t.name} ({t.seats} Pers.) {!isTableAvailable(t.id, formData.date, formData.time, formData.duration) ? '(Besetzt)' : ''}
                      </option>
                    );
                  })}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <ChevronDown size={16} />
                </div>
              </div>
              {activePackage && (
                <p className="text-[10px] text-emerald-600 font-bold mt-1 uppercase tracking-tight flex items-center gap-1">
                  <CheckCircle size={10}/> Filter aktiv: Nur {categories.find(c => c.id === activePackage.categoryId)?.name} Spielfelder
                </p>
              )}
            </div>

            {/* Pakete direkt unterhalb der Spielfeldwahl */}
            {config?.packages && config.packages.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button 
                  onClick={() => setIsPackagesExpanded(!isPackagesExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between group hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-emerald-100 text-emerald-600 rounded flex items-center justify-center">
                      <Gift size={14}/>
                    </div>
                    <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Verfügbare Pakete & Specials</h4>
                    {selectedPackageId && (
                      <span className="bg-emerald-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-black">Aktiv</span>
                    )}
                  </div>
                  {isPackagesExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </button>
                
                {isPackagesExpanded && (
                  <div className="p-3 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-2 animate-fade-in border-t border-slate-100 mt-2 pt-3">
                    {config.packages.map(pkg => (
                      <div key={pkg.id} className="relative group/pkg">
                        <button
                          onClick={() => handlePackageSelect(pkg)}
                          className={`w-full p-3 rounded-xl border-2 transition-all flex flex-col text-left relative overflow-hidden ${selectedPackageId === pkg.id ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-slate-100 hover:border-slate-200'}`}
                        >
                          <div className="flex items-center gap-2 mb-1 pr-6">
                            <span className="text-lg">{pkg.icon}</span>
                            <span className={`text-[10px] font-black uppercase truncate ${selectedPackageId === pkg.id ? 'text-emerald-700' : 'text-slate-700'}`}>{pkg.name}</span>
                          </div>
                          <div className="text-[9px] text-slate-400 font-bold flex gap-2">
                            <span className="flex items-center gap-1"><Timer size={8}/> {pkg.durationMinutes}m</span>
                            <span className="flex items-center gap-1"><Users size={8}/> {pkg.defaultPartySize}</span>
                          </div>
                          {selectedPackageId === pkg.id && <CheckCircle size={14} className="absolute bottom-2 right-2 text-emerald-500" />}
                        </button>
                        {pkg.extendedInfo && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setShowExtendedInfoId(pkg.id); }}
                            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-emerald-600 transition-colors"
                            title="Details"
                          >
                            <Info size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl hover:bg-white transition text-xs font-black uppercase tracking-widest">
            Abbrechen
          </button>
          <button 
            onClick={handleSave} 
            disabled={!formData.manualTableId || !formData.name.trim()}
            className="flex-[2] py-3 theme-bg-secondary text-white rounded-xl transition text-xs font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:grayscale"
          >
            {editingReservation ? 'Änderungen speichern' : 'Buchung bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
};
